import { useEffect, useState } from "react";
import { PageSEO } from "@/components/seo/PageSEO";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { FileDown, ExternalLink, Shield, Clock, CreditCard, Scale, AlertTriangle, Handshake } from "lucide-react";
import { motion } from "framer-motion";

interface DocItem {
  id: string;
  title: string;
  description: string | null;
  file_url: string;
  file_name: string;
  category: string;
}

function DocumentLink({ doc }: { doc: DocItem }) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.open(doc.file_url, "_blank", "noopener,noreferrer");
  };

  return (
    <a
      href={doc.file_url}
      onClick={handleClick}
      className="flex items-center gap-4 bg-card border rounded-xl p-5 hover:shadow-lg transition-all group cursor-pointer"
    >
      <div className="p-3 rounded-xl bg-coral/10 shrink-0">
        <FileDown className="h-6 w-6 text-coral" />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className="font-semibold text-foreground text-base">{doc.title}</h4>
        {doc.description && (
          <p className="text-sm text-muted-foreground mt-0.5">{doc.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">{doc.file_name}</p>
      </div>
      <div className="flex items-center gap-2 text-sm font-medium text-coral shrink-0 group-hover:gap-3 transition-all">
        Öppna <ExternalLink className="h-4 w-4" />
      </div>
    </a>
  );
}

const termsSummary = [
  {
    icon: Handshake,
    title: "Avtalstid & uppsägning",
    description: "Avtal löper enligt vald bindningstid och förnyas automatiskt om det inte sägs upp skriftligen senast 3 månader före periodens slut.",
  },
  {
    icon: CreditCard,
    title: "Betalning & priser",
    description: "Fakturering sker i förskott. Priser justeras årligen och kommuniceras i förväg via hemsidan.",
  },
  {
    icon: Shield,
    title: "Data & sekretess",
    description: "Geposit behandlar kunddata konfidentiellt och i enlighet med GDPR. Kunddata delas aldrig med tredje part utan samtycke.",
  },
  {
    icon: Scale,
    title: "Ansvar & garantier",
    description: "Geposit ansvarar för att tjänsten fungerar enligt specifikation med viss ansvarsbegränsning. Se fullständiga villkor för mer information.",
  },
  {
    icon: Clock,
    title: "Tillgänglighet & SLA",
    description: "Tjänsten tillhandahålls med hög tillgänglighet. Planerade driftstopp aviseras i förväg och underhållsfönster sker utanför kontorstid.",
  },
  {
    icon: AlertTriangle,
    title: "Användning & begränsningar",
    description: "Tjänsten får användas inom ramen för avtalet. Missbruk, automatiserad datautvinning eller vidaredelning/försäljning utan avtal är ej tillåtet.",
  },
];

export default function Villkor() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    supabase
      .from("documents")
      .select("id, title, description, file_url, file_name, category")
      .eq("published", true)
      .order("sort_order")
      .then(({ data }) => {
        if (data) setDocs(data as unknown as DocItem[]);
        setLoaded(true);
      });
  }, []);

  return (
    <div>
      <PageSEO title={t("information.documents.title")} description={t("seo.villkor")} />

      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full border border-primary-foreground/5" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full border border-primary-foreground/5 translate-y-1/2" />

        <div className="relative container mx-auto px-4 lg:px-8 text-center max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-4">
              Dokument & villkor
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              Tydliga villkor för ett tryggt samarbete
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed">
              Här hittar du en sammanfattning av de viktigaste punkterna i våra villkor samt möjlighet att ladda ner de fullständiga dokumenten.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Summary */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              Sammanfattning av våra villkor
            </h2>
            <p className="mt-4 text-muted-foreground font-body text-lg">
              Nedan sammanfattas de viktigaste punkterna. De fullständiga villkoren finns att ladda ner längre ned på sidan.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {termsSummary.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-card rounded-2xl border border-border p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-primary dark:text-white" />
                </div>
                <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center text-sm text-muted-foreground mt-10 max-w-xl mx-auto"
          >
            Ovanstående är en förenklad sammanfattning och ersätter inte de fullständiga villkoren. Vid eventuella avvikelser gäller alltid det signerade avtalet.
          </motion.p>
        </div>
      </section>

      {/* Download Documents */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              Ladda ner fullständiga villkor
            </h2>
            <p className="mt-4 text-muted-foreground font-body text-lg">
              Här kan du ladda ner våra fullständiga avtalsdokument i PDF-format.
            </p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {!loaded ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : docs.length === 0 ? (
              <p className="text-muted-foreground text-center">Inga dokument tillgängliga just nu.</p>
            ) : (
              <div className="space-y-4">
                {docs.map((doc, index) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <DocumentLink doc={doc} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 lg:py-28 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading tracking-tight">
              Frågor om våra villkor?
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/70 font-body">
              Hör av dig så hjälper vi dig att förstå hur villkoren gäller för just din verksamhet.
            </p>
            <div className="mt-8">
              <a
                href="/kontakt"
                className="inline-flex items-center gap-2 bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8 py-3 rounded-md transition-colors"
              >
                Kontakta oss
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
